import {
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useDisclosure,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Plural, Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Download,
  Plus,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
} from 'react';

import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { useSearchParams } from 'react-router-dom';

import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';

import { SortOrder, SortConfig } from '@duke-hq/lib/pagination';
import type { NatFileKey } from '@duke-hq/avetmiss/NATRules';
import { getValidationDescription } from '@duke-hq/avetmiss/validation/duplicateValidation';
import type { ValidationError } from '@duke-hq/avetmiss/validation/fieldValidation';
import type { NatRuleColumn, DuplicateRowInfo, AvetmissResponse } from '@duke-hq/avetmiss/NATTypes';

import { NATColumnFilterPopover } from './NATColumnFilterPopover';
import { NATFileRulesModal } from './NATFileRulesModal';
import { NATTableBody } from './NATTableBody';
import { NATTableHeader } from './NATTableHeader';
import { NATConfirmationModal } from './NATConfirmationModal';
import { NATRowDetails } from './NATRowDetails';
import { formatDuration } from './timing';
import { rowHasLinkableIdentifier } from './rowLinkTargets';
import { iconSize, menuMaxHeight, pxToRem } from '@duke-hq/ui/constants/scalable';

type RowFilter =
  | 'all'
  | 'errors'
  | 'warnings'
  | 'duplicates'
  | 'crossFileErrors'
  | 'crossFileWarnings';

const ROW_FILTERS = [
  'all',
  'errors',
  'warnings',
  'duplicates',
  'crossFileErrors',
  'crossFileWarnings',
] as const satisfies readonly RowFilter[];

function isRowFilter(value: string): value is RowFilter {
  return (ROW_FILTERS as readonly string[]).includes(value);
}

function parseFilter(value: string | undefined): RowFilter {
  return value != null && isRowFilter(value) ? value : 'all';
}

export interface CrossFileReferenceForRow {
  rowIndex: number;
  rule: string;
  missingKey: string;
  severity: 'error' | 'warning';
  targetFile: string;
}

type NATReportTableProps = {
  title: string;
  columns: Record<string, NatRuleColumn>;
  response: AvetmissResponse | undefined;
  isFetching: boolean;
  createNatFile: (data: { rows: Record<string, string>[] }) => void;
  natFileId?: NatFileKey;
  generatedInMs?: number;
  onRegenerate?: () => void;
  crossFileValidationAvailable?: boolean;
  crossFileErrorCount?: number;
  crossFileWarningCount?: number;
  crossFileErrorSourceRowIndices?: number[];
  crossFileWarningSourceRowIndices?: number[];
  crossFileReferences?: CrossFileReferenceForRow[];
  initialFilter?: string;
};

type FilterConfig = Partial<Record<string, string>>;

export function NATReportTable({
  title,
  columns,
  response,
  isFetching,
  createNatFile,
  natFileId,
  generatedInMs,
  onRegenerate,
  crossFileValidationAvailable,
  crossFileErrorCount,
  crossFileWarningCount,
  crossFileErrorSourceRowIndices,
  crossFileWarningSourceRowIndices,
  crossFileReferences,
  initialFilter,
}: NATReportTableProps): React.ReactElement {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({});
  const [selectedColumn, setSelectedColumn] = useState<keyof typeof columns | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [renderList, setRenderList] = useState(false);
  const [rowFilter, setRowFilter] = useState<RowFilter>(() => parseFilter(initialFilter));

  useEffect(() => {
    setRowFilter(parseFilter(initialFilter));
  }, [initialFilter]);

  const [, setSearchParams] = useSearchParams();
  const syncRowFilterToUrl = useCallback(
    (next: RowFilter) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next === 'all') {
            updated.delete('filter');
          } else {
            updated.set('filter', next);
          }
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const {
    isOpen: isRulesModalOpen,
    onOpen: onRulesModalOpen,
    onClose: onRulesModalClose,
  } = useDisclosure();

  const {
    isOpen: isConfirmationModalOpen,
    onOpen: onConfirmationModalOpen,
    onClose: onConfirmationModalClose,
  } = useDisclosure();

  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const [selectedRowData, setSelectedRowData] = useState<{
    rowData: Record<string, string | undefined>;
    originalRowIndex: number;
  } | null>(null);

  const parentRef = useRef<HTMLDivElement>(null);

  const tableData = useMemo(() => {
    if (response?.data?.rows == null || response?.data?.headers == null) {
      return [];
    }

    return response.data.rows.map((row: string[]) => {
      const rowData: Record<string, string> = {};
      response.data.headers.forEach((header: string, headerIndex: number) => {
        const foundKey = Object.keys(columns).find((key) => columns[key]?.name === header);
        const columnKey =
          typeof foundKey === 'string' && foundKey.length > 0
            ? foundKey
            : header.toLowerCase().replace(/\s+/g, '');
        rowData[columnKey] = row[headerIndex] ?? '';
      });

      return rowData;
    });
  }, [response, columns]);

  const originalIndices = useMemo(() => {
    if (response?.data?.rows == null) {
      return [];
    }
    return response.data.rows.map((_, index) => index);
  }, [response]);

  const matchingRowIndices = useMemo<Set<number> | null>(() => {
    if (rowFilter === 'all') {
      return null;
    }

    if (rowFilter === 'errors') {
      return new Set(
        response?.errors
          .filter((entry: ValidationError) => entry.severity === 'error')
          .map((entry: ValidationError) => entry.rowIndex) ?? [],
      );
    }

    if (rowFilter === 'warnings') {
      return new Set(
        response?.errors
          .filter((entry: ValidationError) => entry.severity === 'warning')
          .map((entry: ValidationError) => entry.rowIndex) ?? [],
      );
    }

    if (rowFilter === 'crossFileErrors') {
      return new Set(crossFileErrorSourceRowIndices ?? []);
    }

    if (rowFilter === 'crossFileWarnings') {
      return new Set(crossFileWarningSourceRowIndices ?? []);
    }

    return new Set(
      (response && 'rowInfo' in response.duplicates ? response.duplicates.rowInfo : [])
        .filter((info: DuplicateRowInfo) => info.isDuplicate)
        .map((info: DuplicateRowInfo) => info.rowIndex),
    );
  }, [rowFilter, response, crossFileErrorSourceRowIndices, crossFileWarningSourceRowIndices]);

  const searchHaystacks = useMemo(
    () => tableData.map((row) => Object.values(row).join('').toLowerCase()),
    [tableData],
  );

  const normalisedSearch = searchQuery.trim().toLowerCase();

  const filteredByErrors = useMemo(() => {
    if (matchingRowIndices == null && normalisedSearch === '') {
      return tableData;
    }
    return tableData.filter((_, index) => {
      const originalIndex = originalIndices[index];
      if (matchingRowIndices != null) {
        if (originalIndex === undefined || !matchingRowIndices.has(originalIndex)) {
          return false;
        }
      }
      if (normalisedSearch !== '') {
        const haystack = searchHaystacks[index];
        if (haystack == null || !haystack.includes(normalisedSearch)) {
          return false;
        }
      }
      return true;
    });
  }, [tableData, matchingRowIndices, originalIndices, normalisedSearch, searchHaystacks]);

  const filteredIndices = useMemo(() => {
    if (matchingRowIndices == null && normalisedSearch === '') {
      return originalIndices;
    }
    return originalIndices.filter((originalIndex, index) => {
      if (originalIndex === undefined) {
        return false;
      }
      if (matchingRowIndices != null && !matchingRowIndices.has(originalIndex)) {
        return false;
      }
      if (normalisedSearch !== '') {
        const haystack = searchHaystacks[index];
        if (haystack == null || !haystack.includes(normalisedSearch)) {
          return false;
        }
      }
      return true;
    });
  }, [originalIndices, matchingRowIndices, normalisedSearch, searchHaystacks]);

  const sortedData = useMemo(() => {
    const dataToSort = filteredByErrors;
    const indicesToSort = filteredIndices;

    if (!sortConfig) {
      return {
        values: dataToSort,
        indices: indicesToSort,
      };
    }

    const valueIndexPairs = dataToSort.map((value, index) => ({
      value,
      originalIndex: indicesToSort[index] ?? index,
    }));

    const sortedPairs = valueIndexPairs.sort((a, b) => {
      const aValue = a.value[sortConfig.key] ?? '';
      const bValue = b.value[sortConfig.key] ?? '';

      if (aValue < bValue) {
        return sortConfig.direction === SortOrder.ASC ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === SortOrder.ASC ? 1 : -1;
      }
      return 0;
    });

    return {
      values: sortedPairs.map((pair) => pair.value),
      indices: sortedPairs.map((pair) => pair.originalIndex),
    };
  }, [filteredByErrors, filteredIndices, sortConfig]);

  const filteredData = useMemo(() => {
    if (!Object.keys(filterConfig).length) {
      return sortedData;
    }

    const filteredValues: Record<string, string>[] = [];
    const filteredIndices: number[] = [];

    sortedData.values.forEach((row, index) => {
      const matchesFilter = Object.entries(filterConfig).every(([key, value]) => {
        const rowValue = typeof row[key] === 'string' ? row[key].toLowerCase() : '';
        return value != null ? rowValue.includes(value.toLowerCase()) : false;
      });

      if (matchesFilter) {
        filteredValues.push(row);
        const idx = sortedData.indices[index];
        if (typeof idx === 'number') {
          filteredIndices.push(idx);
        }
      }
    });

    return {
      values: filteredValues,
      indices: filteredIndices,
    };
  }, [sortedData, filterConfig]);

  const isDuplicateRow = useCallback(
    (rowIndex: number) => {
      if (!response || !('rowInfo' in response.duplicates)) {
        return false;
      }

      const duplicateInfo = response.duplicates.rowInfo.find(
        (info: DuplicateRowInfo) => info.rowIndex === rowIndex,
      );

      return duplicateInfo?.isDuplicate ?? false;
    },
    [response],
  );

  const hasRowFieldErrors = useCallback(
    (rowIndex: number) => {
      return (
        response?.errors.some((error: ValidationError) => error.rowIndex === rowIndex) ?? false
      );
    },
    [response],
  );

  const getFieldErrors = useCallback(
    (rowIndex: number, field: string) => {
      return (
        response?.errors.filter(
          (error: ValidationError) => error.rowIndex === rowIndex && error.field === field,
        ) ?? []
      );
    },
    [response],
  );

  const crossFileIssuesByRow = useMemo(() => {
    const map = new Map<number, CrossFileReferenceForRow[]>();
    (crossFileReferences ?? []).forEach((ref) => {
      const existing = map.get(ref.rowIndex);
      if (existing) {
        existing.push(ref);
      } else {
        map.set(ref.rowIndex, [ref]);
      }
    });
    return map;
  }, [crossFileReferences]);

  const getCrossFileIssues = useCallback(
    (rowIndex: number) => {
      return crossFileIssuesByRow.get(rowIndex) ?? [];
    },
    [crossFileIssuesByRow],
  );

  const handleSort = useCallback((key: string) => {
    setSortConfig((prev) =>
      prev?.key === key && prev.direction === SortOrder.ASC
        ? { key, direction: SortOrder.DESC }
        : { key, direction: SortOrder.ASC },
    );
  }, []);

  const handlePopoverCancel = useCallback(() => {
    setSelectedColumn(null);
  }, []);

  const handleColumnFilterApply = useCallback(
    (value: string) => {
      if (selectedColumn != null) {
        setFilterConfig((prev) => ({
          ...prev,
          [selectedColumn]: value,
        }));
        setSelectedColumn(null);
      }
    },
    [selectedColumn],
  );

  const handleRemoveFilter = useCallback((columnKey: string) => {
    setFilterConfig((prev) => {
      const { [columnKey]: _, ...newFilter } = prev;
      return newFilter;
    });
  }, []);

  const handleCreateNatFile = useCallback(() => {
    createNatFile({ rows: filteredData.values });
    onConfirmationModalClose();
  }, [createNatFile, filteredData.values, onConfirmationModalClose]);

  const handleToggleFilter = useCallback(
    (target: 'errors' | 'warnings' | 'duplicates' | 'crossFileErrors' | 'crossFileWarnings') => {
      setRowFilter((prev) => {
        const next = prev === target ? 'all' : target;
        syncRowFilterToUrl(next);
        return next;
      });
    },
    [syncRowFilterToUrl],
  );

  const clearRowFilter = useCallback(() => {
    setRowFilter('all');
    syncRowFilterToUrl('all');
  }, [syncRowFilterToUrl]);

  const handleRowClick = useCallback(
    (originalRowIndex: number, rowData: Record<string, string | undefined>) => {
      const hasFieldErrors = hasRowFieldErrors?.(originalRowIndex) ?? false;
      const isRowDuplicate = isDuplicateRow?.(originalRowIndex) ?? false;
      const hasCrossFileIssues = (crossFileIssuesByRow.get(originalRowIndex)?.length ?? 0) > 0;

      if (
        hasFieldErrors ||
        isRowDuplicate ||
        hasCrossFileIssues ||
        rowHasLinkableIdentifier(rowData)
      ) {
        setSelectedRowData({ rowData, originalRowIndex });
        onDrawerOpen();
      }
    },
    [onDrawerOpen, hasRowFieldErrors, isDuplicateRow, crossFileIssuesByRow],
  );

  const handleDrawerClose = useCallback(() => {
    setSelectedRowData(null);
    onDrawerClose();
  }, [onDrawerClose]);

  useLayoutEffect(() => {
    setRenderList(true);
  }, []);

  const errorCount = response?.errors.filter((error) => error.severity === 'error').length ?? 0;
  const warningCount = response?.errors.filter((error) => error.severity === 'warning').length ?? 0;
  const duplicateCount =
    response && 'duplicateCount' in response.duplicates ? response.duplicates.duplicateCount : 0;
  const totalRecords = response?.data.rows.length ?? 0;
  const hasDuplicates = duplicateCount > 0;

  return (
    <>
      <Drawer isOpen={isDrawerOpen} onClose={handleDrawerClose} size="xl">
        <DrawerOverlay />
        <DrawerContent maxH="100vh" overflow="hidden" display="flex" flexDirection="column">
          <Box flex="1" overflowY="auto" overflowX="hidden">
            <Suspense fallback={<CenteredSpinner />}>
              {selectedRowData && natFileId ? (
                <NATRowDetails
                  natType={natFileId}
                  rowData={selectedRowData.rowData}
                  originalRowIndex={selectedRowData.originalRowIndex}
                  columns={columns}
                  errors={response?.errors || []}
                  crossFileIssues={getCrossFileIssues(selectedRowData.originalRowIndex)}
                  isDuplicateRow={isDuplicateRow?.(selectedRowData.originalRowIndex) ?? false}
                  onClose={handleDrawerClose}
                />
              ) : null}
            </Suspense>
          </Box>
        </DrawerContent>
      </Drawer>

      <Box>
        <Flex p="4" direction="column" gap={4}>
          <Flex
            align={{ base: 'flex-start', md: 'center' }}
            justify="space-between"
            mb={4}
            direction={{ base: 'column', md: 'row' }}
            gap={4}
          >
            <Box>
              <Flex align="center" gap={3}>
                <Text fontSize="lg" fontWeight="bold">
                  {title}
                </Text>
                <Button
                  size="xs"
                  variant="link"
                  colorScheme="brand"
                  leftIcon={<ClipboardList size={iconSize.sm} />}
                  onClick={onRulesModalOpen}
                >
                  <Trans>Rules</Trans>
                </Button>
              </Flex>

              <HStack mt={2} spacing={3}>
                <Text fontSize="sm" color="fg.muted">
                  <Trans>{totalRecords} records</Trans>
                </Text>
                {errorCount === 0 &&
                  warningCount === 0 &&
                  hasDuplicates !== true &&
                  (crossFileErrorCount ?? 0) === 0 &&
                  (crossFileWarningCount ?? 0) === 0 && (
                    <Flex align="center" gap={1.5} fontSize="sm" color="green.600">
                      <Icon as={CheckCircle2} boxSize={iconSize.sm} />
                      <Text>
                        <Trans>No issues</Trans>
                      </Text>
                    </Flex>
                  )}
              </HStack>

              {generatedInMs != null && (
                <Flex mt={1} align="center" gap={2} fontSize="xs" color="fg.muted">
                  <Text>
                    <Trans>Generated in {formatDuration(generatedInMs)}</Trans>
                  </Text>
                  {onRegenerate != null && (
                    <Button size="xs" variant="link" colorScheme="brand" onClick={onRegenerate}>
                      <Trans>Regenerate</Trans>
                    </Button>
                  )}
                </Flex>
              )}
            </Box>

            <Flex align="center" gap={2}>
              <SearchInput
                urlSync={false}
                placeholder={t`Search`}
                width={pxToRem(220)}
                flexShrink={0}
                onCommit={setSearchQuery}
              />
              {(() => {
                const totalCrossFileErrors =
                  crossFileValidationAvailable === true ? (crossFileErrorCount ?? 0) : 0;
                const totalCrossFileWarnings =
                  crossFileValidationAvailable === true ? (crossFileWarningCount ?? 0) : 0;
                const totalIssues =
                  errorCount +
                  warningCount +
                  (hasDuplicates === true ? duplicateCount : 0) +
                  totalCrossFileErrors +
                  totalCrossFileWarnings;

                if (totalIssues === 0) {
                  return null;
                }

                const dominantColor = errorCount > 0 || totalCrossFileErrors > 0 ? 'red' : 'orange';

                return (
                  <Popover placement="bottom-end">
                    <PopoverTrigger>
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme={dominantColor}
                        leftIcon={<AlertTriangle size={iconSize.sm} />}
                        rightIcon={<ChevronDown size={iconSize.sm} />}
                      >
                        <Plural value={totalIssues} one="# issue" other="# issues" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent width={pxToRem(320)}>
                      <PopoverArrow />
                      <PopoverHeader fontSize="sm" fontWeight="semibold">
                        <Trans>Issues breakdown</Trans>
                      </PopoverHeader>
                      <PopoverBody p={0}>
                        <IssueBreakdownRow
                          label={t`In-file errors`}
                          count={errorCount}
                          colorScheme="red"
                          active={rowFilter === 'errors'}
                          onToggle={() => handleToggleFilter('errors')}
                        />
                        <IssueBreakdownRow
                          label={t`In-file warnings`}
                          count={warningCount}
                          colorScheme="orange"
                          active={rowFilter === 'warnings'}
                          onToggle={() => handleToggleFilter('warnings')}
                        />
                        {hasDuplicates === true && (
                          <IssueBreakdownRow
                            label={t`Duplicate rows`}
                            count={duplicateCount}
                            colorScheme="orange"
                            active={rowFilter === 'duplicates'}
                            onToggle={() => handleToggleFilter('duplicates')}
                          />
                        )}
                        {crossFileValidationAvailable === true &&
                          (totalCrossFileErrors > 0 || totalCrossFileWarnings > 0) && (
                            <>
                              <Divider />
                              {(() => {
                                const count = crossFileErrorSourceRowIndices?.length ?? 0;
                                const hint =
                                  count < totalCrossFileErrors
                                    ? t`Filters to ${count} affected rows in this file`
                                    : undefined;
                                return (
                                  <IssueBreakdownRow
                                    label={t`Cross-file errors`}
                                    count={totalCrossFileErrors}
                                    colorScheme="red"
                                    active={rowFilter === 'crossFileErrors'}
                                    hint={hint}
                                    onToggle={
                                      count > 0
                                        ? () => handleToggleFilter('crossFileErrors')
                                        : undefined
                                    }
                                  />
                                );
                              })()}
                              {(() => {
                                const count = crossFileWarningSourceRowIndices?.length ?? 0;
                                const hint =
                                  count < totalCrossFileWarnings
                                    ? t`Filters to ${count} affected rows in this file`
                                    : undefined;
                                return (
                                  <IssueBreakdownRow
                                    label={t`Cross-file warnings`}
                                    count={totalCrossFileWarnings}
                                    colorScheme="orange"
                                    active={rowFilter === 'crossFileWarnings'}
                                    hint={hint}
                                    onToggle={
                                      count > 0
                                        ? () => handleToggleFilter('crossFileWarnings')
                                        : undefined
                                    }
                                  />
                                );
                              })()}
                            </>
                          )}
                        {rowFilter !== 'all' && (
                          <>
                            <Divider />
                            <Box p={2}>
                              <Button
                                size="xs"
                                variant="ghost"
                                width="full"
                                onClick={clearRowFilter}
                              >
                                <Trans>Clear filter, show all rows</Trans>
                              </Button>
                            </Box>
                          </>
                        )}
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                );
              })()}
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="outline"
                  leftIcon={<Plus size={iconSize.md} />}
                >
                  <Trans>Add Filter</Trans>
                </MenuButton>
                <MenuList maxH={menuMaxHeight} overflowY="auto">
                  {Object.keys(columns).map((key) => (
                    <MenuItem key={key} onClick={() => setSelectedColumn(key)}>
                      {columns[key]?.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>

              <Button
                size="sm"
                colorScheme="brand"
                leftIcon={<Icon as={Download} />}
                onClick={() =>
                  errorCount > 0 || warningCount > 0 || hasDuplicates
                    ? onConfirmationModalOpen()
                    : handleCreateNatFile()
                }
                isDisabled={filteredData.values.length === 0}
              >
                <Trans>Export</Trans>
              </Button>
            </Flex>
          </Flex>

          {natFileId != null && (
            <Flex align="stretch" p={2} bg="gray.50" borderRadius="md">
              <Text fontSize="sm" color="gray.600">
                {getValidationDescription(natFileId)}
              </Text>
            </Flex>
          )}

          <Flex position="relative">
            {selectedColumn != null ? (
              <NATColumnFilterPopover
                columnName={columns[selectedColumn]?.name ?? ''}
                onApply={handleColumnFilterApply}
                onCancel={handlePopoverCancel}
              />
            ) : null}
          </Flex>

          {Object.keys(filterConfig).length > 0 && (
            <Box mb={4}>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                <Trans>Active Filters:</Trans>
              </Text>
              <Wrap>
                {Object.entries(filterConfig).map(([columnKey, filterValue]) => (
                  <WrapItem key={columnKey}>
                    <Tag size="sm" variant="subtle">
                      <TagLabel>
                        {columns[columnKey]?.name}: {filterValue}
                      </TagLabel>
                      <TagCloseButton onClick={() => handleRemoveFilter(columnKey)} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}
        </Flex>

        <Box ref={parentRef} maxH="70vh" overflowY="auto" bg="bg.surface" position="relative">
          {isFetching ? (
            <Center p={8}>
              <Spinner />
            </Center>
          ) : renderList ? (
            filteredData.values.length === 0 ? (
              <Center p={8}>
                <Text fontSize="sm" color="muted">
                  <Trans>No records to display</Trans>
                </Text>
              </Center>
            ) : (
              <Table>
                <NATTableHeader columns={columns} sortConfig={sortConfig} onSort={handleSort} />
                <NATTableBody
                  parentRef={parentRef}
                  columns={columns}
                  values={filteredData.values}
                  onError={() => {}} // No-op for now
                  isDuplicateRow={isDuplicateRow}
                  hasRowFieldErrors={hasRowFieldErrors}
                  getFieldErrors={getFieldErrors}
                  getCrossFileIssues={getCrossFileIssues}
                  originalRowIndices={filteredData.indices}
                  onRowClick={handleRowClick}
                />
              </Table>
            )
          ) : (
            <Box p={3}>
              <Text fontSize="sm" textAlign="center">
                <Trans>No data found.</Trans>
              </Text>
            </Box>
          )}
        </Box>

        <NATFileRulesModal
          isOpen={isRulesModalOpen}
          onClose={onRulesModalClose}
          columns={columns}
          description={natFileId ? getValidationDescription(natFileId) : null}
        />

        <NATConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={onConfirmationModalClose}
          onConfirm={handleCreateNatFile}
          title={t`Download NAT File`}
          message={t`Are you sure you want to download this NAT file with ${filteredData.values.length} records? Note: This file contains validation errors that should be addressed.`}
        />
      </Box>
    </>
  );
}

type IssueBreakdownRowProps = {
  label: string;
  count: number;
  colorScheme: 'red' | 'orange';
  active: boolean;
  hint?: string;
  onToggle?: () => void;
};

function IssueBreakdownRow({
  label,
  count,
  colorScheme,
  active,
  hint,
  onToggle,
}: IssueBreakdownRowProps) {
  const isClickable = onToggle != null && count > 0;
  const dimmed = count === 0;

  const content = (
    <Flex
      align="center"
      justify="space-between"
      gap={2}
      px={3}
      py={2}
      width="full"
      bg={active ? `${colorScheme}.subtle` : undefined}
      _hover={isClickable ? { bg: active ? `${colorScheme}.muted` : 'bg.muted' } : undefined}
      opacity={dimmed ? 0.5 : 1}
    >
      <Flex direction="column" align="flex-start" gap={0}>
        <Text fontSize="sm" fontWeight={active ? 'semibold' : 'normal'}>
          {label}
        </Text>
        {hint != null && (
          <Text fontSize="xs" color="fg.muted">
            {hint}
          </Text>
        )}
      </Flex>
      <Badge colorScheme={count > 0 ? colorScheme : 'gray'} fontSize="xs" variant="subtle">
        {count}
      </Badge>
    </Flex>
  );

  if (!isClickable) {
    return content;
  }

  return (
    <Box
      as="button"
      type="button"
      onClick={onToggle}
      width="full"
      textAlign="left"
      cursor="pointer"
    >
      {content}
    </Box>
  );
}
